<script setup lang="ts">
  import logger from '@leaflink/snitch';
  import { computed, ref } from 'vue';

  import type { LinkTokenResponse, Plaid } from '../../../types/plaid';
  import usePlaidLink from '../../composables/usePlaidLink/usePlaidLink';

  export interface PlaidLinkProps {
    /**
     * @deprecated Plaid does not support other URLs for loading its SDK
     * Plaid initialization script url to be used
     */
    initScriptUrl?: string;

    /**
     * Key to be used in session storage for OAuth flow
     */
    storageKey?: string;

    /**
     * Url to be used to redirect the user from the OAuth flow
     */
    returnTo?: string;

    /**
     * Plaid link token
     */
    token?: string;

    /**
     * Plaid environment to connect
     */
    env?: Plaid.Environment;

    /**
     * Function that returns a link token object to be used with Plaid.
     * `link_token` takes precedence over `token` if informed.
     */
    createToken?: () => Promise<LinkTokenResponse>;
  }

  const props = withDefaults(defineProps<PlaidLinkProps>(), {
    initScriptUrl: 'https://cdn.plaid.com/link/v2/stable/link-initialize.js',
    storageKey: undefined,
    returnTo: window.location.href,
    env: 'sandbox',
    token: undefined,
    createToken: undefined,
  });

  const internalStorageKey = computed(() => {
    if (props.storageKey) {
      return props.storageKey;
    }

    // returns up to the last 3 parts of the host. e.g. for `subdomain.my.domain.com` it will return `my.domain.com`
    const host = window.location.host.split('.').splice(-3).join('.');

    return `${host}-plaid`;
  });

  const emit = defineEmits<{
    /**
     * Emitted whenever the exit event contains errors
     */
    (e: 'error', error: Plaid.Error | null | Error, metadata?: Plaid.OnExitMetaData): void;
    /**
     * Emitted whenever the user successfully links an item
     */
    (e: 'success', public_token: string, metadata: Plaid.OnSuccessMetaData): void;
    /**
     * Emitted whenever the user exits the Link flow
     */
    (e: 'exit', metadata: Plaid.OnExitMetaData): void;
    /**
     * Emitted when the Link is ready to be opened
     */
    (e: 'load'): void;
    /**
     * Emitted at specific points during the Link flow
     */
    (e: 'event', eventName: Plaid.EventName, metadata: Plaid.OnEventMetaData): void;
  }>();

  const isPlaidModalOpen = ref(false);

  const onExit: Plaid.OnExit = (error: Plaid.Error | null, metadata: Plaid.OnExitMetaData) => {
    isPlaidModalOpen.value = false;

    if (error) {
      logger.error(new Error('Plaid: Error trying to link an account'), {
        tags: { plaid: true },
        extra: { PlaidError: error },
      });
      emit('error', error, metadata);
    }

    sessionStorage.removeItem(internalStorageKey.value);

    emit('exit', metadata);
  };

  const onSuccess: Plaid.OnSuccess = (public_token: string, metadata: Plaid.OnSuccessMetaData) => {
    isPlaidModalOpen.value = false;

    sessionStorage.removeItem(internalStorageKey.value);

    emit('success', public_token, metadata);
  };

  const onLoad: Plaid.OnLoad = (...args) => emit('load', ...args);

  const onEvent: Plaid.OnEvent = (...args) => emit('event', ...args);

  const plaidLinkOptions = ref<Plaid.CreateConfig>({
    env: props.env,
    onSuccess,
    onEvent,
    onLoad,
    onExit,
  });

  const { open: openPlaidLink } = usePlaidLink(plaidLinkOptions);

  async function handleClick() {
    if (isPlaidModalOpen.value === true) {
      return;
    }

    isPlaidModalOpen.value = true;

    if (typeof props.createToken === 'function') {
      try {
        const { link_token } = await props.createToken();

        plaidLinkOptions.value.token = link_token;
      } catch (err) {
        const error = new Error('Plaid: Error trying to invoke createToken');

        logger.error(error, {
          tags: { plaid: true },
          extra: { error: err },
        });

        emit('error', error);
      }
    } else if (props.token) {
      plaidLinkOptions.value.token = props.token;
    }

    if (!plaidLinkOptions.value.token) {
      const error = new Error('Plaid: link token not found');

      logger.error(error, { tags: { plaid: true } });
      emit('error', error);

      isPlaidModalOpen.value = false;

      return;
    }

    sessionStorage.setItem(
      internalStorageKey.value,
      JSON.stringify({ token: plaidLinkOptions.value.token, returnTo: props.returnTo, env: props.env }),
    );

    openPlaidLink();
  }

  defineExpose({
    handleClick,
  });
</script>

<template>
  <span data-test="stash-plaid-link" @click="handleClick">
    <!-- @slot Default slot to trigger Plaid's modal -->
    <slot :open="isPlaidModalOpen"></slot>
  </span>
</template>
